import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  workLevelListService,
  workLevelDetailService,
  workLevelCreateService,
  workLevelUpdateService,
  workLevelListShortService,
} from "./services";

type WorkLevelListResponse = Awaited<ReturnType<typeof workLevelListService>>;
type WorkLevelShortListResponse = Awaited<
  ReturnType<typeof workLevelListShortService>
>;
type WorkLevelDetailResponse = Awaited<
  ReturnType<typeof workLevelDetailService>
>;

export const useWorkLevels = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof workLevelListService>[0];
  options?: QueryObserverOptions<unknown, unknown, WorkLevelListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "workLevelListHooks",
    queryFn: () => workLevelListService(params),
    ...options,
  });

  return response as UseQueryResult<WorkLevelListResponse>;
};

export const useShortWorkLevels = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof workLevelListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, WorkLevelShortListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "workLevelShortListHooks",
    queryFn: () => workLevelListShortService(params),
    ...options,
  });

  return response as UseQueryResult<WorkLevelShortListResponse>;
};

export const useWorkLevelDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof workLevelDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, WorkLevelDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "workLevelDetailHooks",
    queryFn: () => workLevelDetailService(params),
    ...options,
  });

  return response as UseQueryResult<WorkLevelDetailResponse>;
};

export const useWorkLevelUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    WorkLevelDetailResponse,
    unknown,
    Parameters<typeof workLevelUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "workLevelUpdateHooks",
    mutationFn: workLevelUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useWorkLevelCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    WorkLevelDetailResponse,
    unknown,
    Parameters<typeof workLevelCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "workLevelCreateHooks",
    mutationFn: workLevelCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
